import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  camelCase, compose, dropRight,
  find, findIndex, get,
  isEmpty, last, size,
  some, toNumber, toString,
  upperFirst,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { Button, OutlinedInput, Typography } from '@material-ui/core';
import { Replay } from '@material-ui/icons';
import messages from '../messages';

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  Instruction: styled(Typography)`
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 700;
  `,
  Row: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    order: -1;
    margin-bottom: 1.5rem;
  `,
  Info: styled(Typography)`
    margin-right: auto;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(12)};
  `,
  Number: styled.span`
    margin-right: 4px;
  `,
  Type: styled.span`
    margin-left: 4px;
  `,
  OtherResponseButton: styled(({ active, ...otherProps }) => <Button {...otherProps} />)`
    min-width: 0;
    min-height: 32px;
    margin-top: ${({ theme }) => theme.typography.pxToRem(26)};
    margin-bottom: ${({ theme }) => theme.typography.pxToRem(26)};
    padding: 3px 8px;
    border-color: ${({ theme }) => theme.palette.grey[300]};
    font-weight: 500;
    line-height: 1.54;
    :last-child {
      margin-left: 8px;
    }
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    :disabled {
      color: ${({ theme }) => theme.palette.grey[400]};
      border-color: ${({ theme }) => theme.palette.grey[300]};
    }
    ${({ variant, theme }) => variant === 'outlined' && css`
      color: ${theme.palette.grey[600]};
    `}
    ${({ active, theme }) => (active ? css`
      color: ${theme.palette.grey[700]};
      background-color: ${theme.palette.hexToRgb('#000', 0.08)};
    ` : css`
      :hover {
        color: ${theme.palette.grey[900]};
        background-color: ${theme.palette.primary.contrastText};
      }
    `)}
  `,
  Button: styled(Button)`
    min-width: 0;
    min-height: 32px;
    padding: 3px 8px;
    &.MuiButton-outlined {
      border-color: ${({ theme }) => theme.palette.grey[300]};
    }
    :last-child {
      margin-left: 8px;
    }
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    :disabled {
      color: ${({ theme }) => theme.palette.primary.contrastText};
    }
    ${({ variant, theme }) => variant === 'outlined' && css`
      color: ${theme.palette.grey[600]};
    `}
  `,
  Helper: styled.div`
    display: flex;
    align-items: center;
    min-height: 32px;
  `,
  HelperText: styled.p`
    margin-right: 8px;
    color: ${({ active, theme }) => (active ? theme.palette.status.info : theme.palette.grey[400])};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 500;
    line-height: 1.53;
  `,
  Field: styled.div`
    display: flex;
    align-items: center;
    margin-top: ${({ theme }) => theme.typography.pxToRem(30)};
    margin-bottom: ${({ theme }) => theme.typography.pxToRem(30)};
  `,
  Label: styled.label`
    margin-right: 0.5rem;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
  `,
  OutlinedInput: styled(({ classes, ...otherProps }) => (
    <OutlinedInput
      classes={{
        input: 'input',
        notchedOutline: 'notchedOutline',
      }}
      {...otherProps}
    />
  ))`
    box-sizing: border-box;
    width: 40px;
    height: 24px;
    margin-right: 4px;
    padding: 4px 8px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    input[type="number"]::-webkit-outer-spin-button, input[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type="number"] {
      -moz-appearance: textfield;
    }
    .input {
      padding: 0;
      text-align: center;
    }
    .notchedOutline {
      border-radius: 2px;
    }
  `,
  Percent: styled.span`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  Actions: styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
  `,
  UndoButton: styled(Button)`
    padding: 5px 8px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
  `,
  Replay: styled(Replay)`
    margin-right: 4px;
    font-size: 1rem;
  `,
};

const AddOptionPaper = ({
  questions, setFieldValue, setStep, type, optionType, setOptionType,
}) => {
  const optionCount = useMemo(
    () => compose(
      size,
      get('options'),
      last,
    )(questions),
    [questions],
  );

  const hasOtherResponseField = useMemo(() => compose(
    some(option => option.type === 'TEXT'),
    get('options'),
    last,
  )(questions), [questions]);

  const goPrevStep = useCallback(() => {
    setStep(prevStep => prevStep - 1);
    setFieldValue(`questions[${size(questions) - 1}].options`, []);
  }, [questions, setFieldValue, setStep]);

  const goNextStep = () => setStep(prevStep => prevStep + 1);

  const undo = useCallback(() => {
    setFieldValue(`questions[${size(questions) - 1}].options`, dropRight(1, last(questions).options));
  }, [questions, setFieldValue]);

  const toggleOptionType = () => setOptionType(prevOptionType => (prevOptionType === 'SELECTION' ? 'TEXT' : 'SELECTION'));

  const handleChange = useCallback((event) => {
    const name = `questions[${size(questions) - 1}].options[${findIndex(option => option.type === 'TEXT', last(questions).options)}].size.width`;

    if (toNumber(event.target.value) > 0 && toNumber(event.target.value) <= 100) {
      setFieldValue(name, toNumber(event.target.value));
    }

    if (toNumber(event.target.value) <= 0) {
      setFieldValue(name, 1);
    }

    if (toNumber(event.target.value) > 100) {
      setFieldValue(name, 100);
    }
  }, [questions, setFieldValue]);

  return (
    <S.Wrapper>
      <S.Instruction>
        <FormattedMessage {...messages.addOptionPaperInstruction} />
      </S.Instruction>
      <S.Row>
        <S.Info>
          <S.Number>
            <FormattedMessage {...messages.addOptionPaperNumber} values={{ number: size(questions) }} />
          </S.Number>
          /
          <S.Type>
            <FormattedMessage {...messages[`addOptionPaperType${upperFirst(camelCase(type))}`]} />
          </S.Type>
        </S.Info>
      </S.Row>
      <S.Helper>
        <S.HelperText active={!isEmpty(get('options', last(questions)))}>
          <FormattedMessage {...messages.addOptionPaperHelperText} values={{ count: optionCount }} />
        </S.HelperText>
        {!isEmpty(get('options', last(questions))) && (
          <S.UndoButton type="button" onClick={undo}>
            <S.Replay />
            <FormattedMessage {...messages.addOptionPaperUndoButton} />
          </S.UndoButton>
        )}
      </S.Helper>
      {get('type', last(get('options', last(questions)))) === 'TEXT'
        ? (
          <S.Field>
            <S.Label htmlFor="otherResponseAreaWidth">
              &apos;기타&apos; 의견란 가로 길이:
            </S.Label>
            <S.OutlinedInput
              id="otherResponseAreaWidth"
              type="number"
              inputProps={{
                min: 1,
                max: 100,
              }}
              value={toString(get('size.width', find(option => option.type === 'TEXT', get('options', last(questions)))))}
              onChange={handleChange}
            />
            <S.Percent>%</S.Percent>
          </S.Field>
        )
        : (
          <S.OtherResponseButton
            type="button"
            variant="outlined"
            active={optionType === 'TEXT'}
            onClick={toggleOptionType}
            disabled={hasOtherResponseField}
          >
            {hasOtherResponseField ? '\'기타\' 의견란 추가됨' : '\'기타\' 의견란 추가'}
          </S.OtherResponseButton>
        )}
      <S.Actions>
        <S.Button type="button" variant="outlined" onClick={goPrevStep}>
          <FormattedMessage {...messages.addOptionPaperButtonPrev} />
        </S.Button>
        <S.Button type="button" color="primary" variant="contained" disabled={isEmpty(get('options', last(questions)))} onClick={goNextStep}>
          <FormattedMessage {...messages.addOptionPaperButtonNext} />
        </S.Button>
      </S.Actions>
    </S.Wrapper>
  );
};

AddOptionPaper.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      options: PropTypes.arrayOf(
        PropTypes.shape({
          number: PropTypes.number.isRequired,
          content: PropTypes.string.isRequired,
          position: PropTypes.shape({
            x: PropTypes.number.isRequired,
            y: PropTypes.number.isRequired,
          }),
        }),
      ),
    }),
  ),
  setFieldValue: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  type: PropTypes.string,
  optionType: PropTypes.oneOf(['SELECTION', 'TEXT']).isRequired,
  setOptionType: PropTypes.func.isRequired,
};

AddOptionPaper.defaultProps = {
  questions: null,
  type: null,
};

export default AddOptionPaper;
